import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { toast } from 'sonner';
import API from '../utils/api';

/**
 * Centered, full-screen-attention incoming-call overlay (purple, white text,
 * Accept / Decline). Listens for `incoming-call-show` and `incoming-call-hide`
 * events fired by useIncomingCallNotifier.
 *
 * On Accept:
 *  1. Persist the room to sessionStorage as the pending join — this is the
 *     handoff vehicle, NOT React-event timing, so the navigation chain works
 *     deterministically regardless of how slowly each page mounts.
 *  2. Navigate to comms → video tab. CommunicationHub & VideoCalls each
 *     check sessionStorage on mount and finish the join.
 */
function IncomingCallOverlay() {
  var [room, setRoom] = useState(null);
  var [acting, setActing] = useState(false);

  useEffect(function () {
    function onShow(e) { setRoom(e && e.detail ? e.detail : null); }
    function onHide() { setRoom(null); }
    window.addEventListener('incoming-call-show', onShow);
    window.addEventListener('incoming-call-hide', onHide);
    return function () {
      window.removeEventListener('incoming-call-show', onShow);
      window.removeEventListener('incoming-call-hide', onHide);
    };
  }, []);

  var accept = useCallback(function () {
    if (!room || acting) return;
    setActing(true);
    // Hand off via sessionStorage so the receiving pages can pick it up
    // whenever they mount, without depending on React event-timing.
    try {
      sessionStorage.setItem('parentum_pending_video_join', JSON.stringify(room));
    } catch (e) { /* ignore */ }
    // Tell Dashboard to switch to Comms.
    window.dispatchEvent(new CustomEvent('navigate-comms-video'));
    setRoom(null);
    setActing(false);
  }, [room, acting]);

  var decline = useCallback(function () {
    if (!room || acting) return;
    setActing(true);
    axios.post(API + '/video/rooms/' + room.room_sid + '/decline', {}, { withCredentials: true })
      .then(function () { toast.success('Call declined'); })
      .catch(function () { /* swallow — refetch reflects truth */ })
      .finally(function () {
        setRoom(null);
        setActing(false);
        window.dispatchEvent(new CustomEvent('incoming-calls-refresh'));
      });
  }, [room, acting]);

  if (!room) return null;

  var caller = room.initiator_name || room.partner_name || 'Co-parent';
  var rec = room.recording_enabled ? 'Recording will be ON' : 'Recording will be OFF';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in"
      style={{ background: 'rgba(15, 12, 40, 0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="incoming-call-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incoming-call-title">
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl text-white p-7 sm:p-9 text-center"
        style={{ background: 'linear-gradient(160deg, #2D2560 0%, #5A4E9E 100%)', color: '#FFFFFF' }}>
        <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ring-4 ring-white/20"
          style={{ background: 'rgba(255,255,255,0.12)' }}>
          <Video className="w-8 h-8 text-white animate-pulse" style={{ color: '#FFFFFF' }} />
        </div>
        <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>Incoming video call</p>
        <h2 id="incoming-call-title" className="text-2xl sm:text-3xl font-bold mt-2 mb-1" style={{ color: '#FFFFFF' }}>{caller}</h2>
        <p className="text-sm mb-7" style={{ color: 'rgba(255,255,255,0.85)' }}>{rec}</p>

        <div className="flex items-center justify-center gap-3">
          <button
            onClick={decline}
            disabled={acting}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-colors disabled:opacity-50 shadow-lg"
            style={{ background: '#EF4444', color: '#2D2560' }}
            data-testid="incoming-call-decline">
            <PhoneOff className="w-4 h-4" style={{ color: '#2D2560' }} /> Decline
          </button>
          <button
            onClick={accept}
            disabled={acting}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white font-semibold text-sm hover:bg-white/95 transition-colors disabled:opacity-50 shadow-lg"
            style={{ color: '#2D2560' }}
            data-testid="incoming-call-accept">
            <Phone className="w-4 h-4" style={{ color: '#2D2560' }} /> Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export default IncomingCallOverlay;
